import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CustomFieldsForm.css';

/**
 * The answers to whatever the form asks for this category.
 *
 * Two faults, which together made this component permanently useless:
 *
 *   It was gated on `token`, read from localStorage('token') - a key nothing
 *   has written since staff sign-in moved to Entra, so always null. The effect
 *   never ran, setLoading(false) was never reached, and the box sat on
 *   "Loading custom fields..." for ever. That is what Gordon was looking at:
 *   not a category with no fields, a fetch that never happened.
 *
 *   It read item.field_id and item.field_value off the saved values. The
 *   endpoint returns custom_field_id and value, so every answer keyed itself
 *   as undefined and no saved value ever appeared in its box.
 *
 * Authentication comes from the axios interceptor. On a ticket that does not
 * exist yet - Create Ticket - there is nothing to save against, so the answers
 * are reported upwards and the page writes them once the ticket has an id.
 */
/*
 * `auth` is an axios config to send with every call - a contractor's portal
 * token (26 October: the standard form is theirs too, and the interceptor
 * only ever signs a member of staff's calls). Undefined for staff, which
 * changes nothing.
 */
const CustomFieldsForm = ({ ticketId, ticketCategory, onValuesChanged, auth }) => {
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch field definitions for this category
  const fetchFieldDefinitions = useCallback(async () => {
    if (!ticketCategory) return [];
    try {
      const response = await axios.get(`${API_BASE}/custom-fields`,
        { ...(auth || {}), params: { category: ticketCategory } });
      const data = response.data;
      const rows = Array.isArray(data) ? data : (data?.custom_fields ?? []);
      setFieldDefinitions(rows);
      setError(null);
      return rows;
    } catch (err) {
      console.error('Error fetching field definitions:', err);
      setError('Could not load the extra fields for this category');
      setFieldDefinitions([]);
      return [];
    }
  }, [ticketCategory, API_BASE, auth]);

  // Fetch field values for this ticket
  const fetchFieldValues = useCallback(async () => {
    if (!ticketId) return;
    try {
      const response = await axios.get(`${API_BASE}/tickets/${ticketId}/custom-field-values`, auth);
      const valuesMap = {};
      (Array.isArray(response.data) ? response.data : []).forEach((item) => {
        // custom_field_id / value are the columns. field_id / field_value were
        // never on the row, so this map used to be { undefined: undefined }.
        valuesMap[item.custom_field_id] = item.value ?? '';
      });
      setFieldValues(valuesMap);
    } catch (err) {
      console.error('Error fetching field values:', err);
    }
  }, [ticketId, API_BASE, auth]);

  // One effect that always finishes. Whatever happens - no category, no
  // fields, a failed request - loading ends, because the alternative is the
  // box Gordon reported sitting there for ever.
  useEffect(() => {
    let live = true;
    (async () => {
      if (!ticketCategory) {
        if (live) { setFieldDefinitions([]); setLoading(false); }
        return;
      }
      if (live) setLoading(true);
      await fetchFieldDefinitions();
      if (ticketId) await fetchFieldValues();
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [ticketCategory, ticketId, fetchFieldDefinitions, fetchFieldValues]);

  const handleFieldChange = (fieldId, value) => {
    setFieldValues((prev) => {
      const next = { ...prev, [fieldId]: value };
      // On a ticket that does not exist yet there is nowhere to save, so the
      // page above is told and writes them once it has an id.
      if (!ticketId && onValuesChanged) onValuesChanged(next);
      return next;
    });
  };

  const handleSaveField = async (fieldId, value) => {
    // Nothing to save against until the ticket exists; the page carries them.
    if (!ticketId) return;
    try {
      setSaving(true);
      await axios.post(`${API_BASE}/tickets/${ticketId}/custom-field-values`,
        { custom_field_id: fieldId, value }, auth);
      setError(null);
      if (onValuesChanged) onValuesChanged(fieldValues);
    } catch (err) {
      console.error('Error saving field value:', err);
      // This used to 404 every time - the endpoint did not exist - and said so
      // nowhere, so an answer typed into a box simply evaporated.
      setError(err.response?.data?.error || 'That field could not be saved');
    } finally {
      setSaving(false);
    }
  };

  // Nothing at all until there is something to show. A category with no extra
  // fields is the normal case, and a box announcing that it is loading nothing
  // is worse than no box.
  if (loading || fieldDefinitions.length === 0) return null;

  const renderField = (field) => {
    const value = fieldValues[field.id] || '';

    const commonProps = {
      value: value,
      onChange: (e) => handleFieldChange(field.id, e.target.value),
      onBlur: () => handleSaveField(field.id, value),
      disabled: saving
    };

    switch (field.field_type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={4}
            placeholder={field.field_label}
          />
        );

      case 'number':
        return (
          <input
            {...commonProps}
            type="number"
            placeholder={field.field_label}
          />
        );

      case 'email':
        return (
          <input
            {...commonProps}
            type="email"
            placeholder={field.field_label}
          />
        );

      case 'date':
        return (
          <input
            {...commonProps}
            type="date"
          />
        );

      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select {field.field_label}</option>
            {field.options && field.options.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={value === 'true' || value === true}
              onChange={(e) => {
                const newValue = e.target.checked ? 'true' : 'false';
                handleFieldChange(field.id, newValue);
                handleSaveField(field.id, newValue);
              }}
              disabled={saving}
            />
            {field.field_label}
          </label>
        );

      case 'text':
      default:
        return (
          <input
            {...commonProps}
            type="text"
            placeholder={field.field_label}
          />
        );
    }
  };

  return (
    <div className="custom-fields-form">
      <h3 className="cf-section-title">Custom Fields</h3>

      {error && (
        <div className="cf-error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="cf-fields-grid">
        {fieldDefinitions.map(field => (
          <div
            key={field.id}
            className={`cf-field-wrapper ${field.field_type === 'textarea' ? 'full-width' : ''}`}
          >
            <label htmlFor={`field-${field.id}`} className="cf-field-label">
              {field.field_label}
              {field.is_required && <span className="required-asterisk">*</span>}
            </label>

            <div className="cf-field-input-wrapper">
              {renderField(field)}
              {saving && <span className="saving-indicator">Saving...</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomFieldsForm;
